"use client";

import React, { useEffect } from "react";
import { MapPin, Phone, Mail, ArrowRight, Loader, Send, Map as MapIcon } from "lucide-react";
import { toast } from "sonner";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Toaster } from "@/components/ui/sonner";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const SHARED_DATA = {
  address: "750 Park Ave, New York, NY",
  phone: "+1 (212) 555-0198",
  email: "ir@n2ncapital.ai",
  businessName: "N2N Capital",
  location: "New York City"
};
export default function ConnectWithUs() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section ref={ref} id="connect" className={`relative py-24 md:py-32 overflow-hidden transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{
      backgroundImage: `linear-gradient(to right, var(--foreground) 1px, transparent 1px), linear-gradient(to bottom, var(--foreground) 1px, transparent 1px)`,
      backgroundSize: '40px 40px'
    }} />

      <div className="container relative z-10 mx-auto px-4 md:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          
          {}
          <div className="space-y-12">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 border border-accent/20 bg-accent/5 text-accent font-mono text-[10px] uppercase tracking-[0.2em]">
                <div className="size-1.5 bg-accent animate-pulse" />
                Direct Channel
              </div>
              <h2 className="text-5xl md:text-6xl font-sans font-bold tracking-tighter text-foreground leading-[0.95]">
                Establish <br />
                <span className="text-accent">Connectivity.</span>
              </h2>
              <p className="text-lg text-muted-foreground font-serif max-w-md leading-relaxed">
                Our investment team maintains active channels for institutional partners, family offices, and infrastructure developers.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-8">
              <div className="space-y-3">
                <h4 className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Headquarters</h4>
                <div className="flex items-start gap-3 group">
                  <MapPin className="size-5 text-accent shrink-0 mt-0.5" />
                  <span className="text-sm font-sans font-medium group-hover:text-accent transition-colors">{SHARED_DATA.address}</span>
                </div>
              </div>
              <div className="space-y-3">
                <h4 className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Inquiries</h4>
                <div className="flex flex-col gap-2">
                  <Link to={`mailto:${SHARED_DATA.email}`} className="flex items-center gap-3 group">
                    <Mail className="size-5 text-accent shrink-0" />
                    <span className="text-sm font-sans font-medium group-hover:text-accent transition-colors">{SHARED_DATA.email}</span>
                  </Link>
                  <Link to={`tel:${SHARED_DATA.phone}`} className="flex items-center gap-3 group">
                    <Phone className="size-5 text-accent shrink-0" />
                    <span className="text-sm font-sans font-medium group-hover:text-accent transition-colors">{SHARED_DATA.phone}</span>
                  </Link>
                </div>
              </div>
            </div>

            <div className="pt-8 border-t border-foreground/5">
              <div className="flex items-center gap-6">
                <div className="flex -space-x-2">
                  {[1, 2, 3].map(i => <div key={i} data-index={i} className="size-10 rounded-none border-2 border-background bg-secondary flex items-center justify-center overflow-hidden">
                      <div className="size-full bg-accent/10 flex items-center justify-center text-accent font-mono text-[10px]">
                        0{i}
                      </div>
                    </div>)}
                </div>
                <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider">
                  Global Infrastructure <br /> Network Active
                </p>
              </div>
            </div>
          </div>

          {}
          <div className="relative">
            <div className="absolute -inset-4 border border-foreground/5 pointer-events-none" />
            <div className="bg-card p-8 md:p-10 shadow-sm border border-foreground/5 relative">
              <div className="mb-8 flex justify-between items-end">
                <h3 className="font-sans font-bold text-2xl tracking-tight">Inquiry Protocol</h3>
                <div className="font-mono text-[10px] text-muted-foreground uppercase">Ref: N2N-SEC-01</div>
              </div>
              
              <FormProvider formId="connect-with-us-main" sectionName="ConnectWithUs" defaultValues={{
              name: "",
              email: "",
              phone: "",
              reason: "",
              message: ""
            }} successMessage="Transmission received. Our team will review your inquiry within 24 hours.">
                <ContactForm />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  useEffect(() => {
    if (isSubmitted && !submitError) {
      toast.success(successMessage, {
        description: "Response time: < 24 hours.",
        duration: 5000
      });
    }
  }, [isSubmitted, submitError, successMessage]);
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-sm bg-primary/10 flex items-center justify-center text-primary mb-2 shadow-[0_0_20px_color-mix(in_srgb,var(--color-primary),transparent_80%)]">
          <Send className="size-8" />
        </div>
        <h4 className="text-xl font-sans font-bold text-foreground uppercase tracking-tight">Transmission Sent</h4>
        <p className="text-muted-foreground max-w-xs mx-auto font-serif">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6 font-mono text-xs uppercase tracking-widest rounded-sm border-white/10">
          New Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Identification required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Investor Name" {...field} className="bg-background/50 border-white/10 rounded-sm focus:ring-primary/50 font-sans" />
              </FormControl>
              <FormMessage className="font-mono text-[10px]" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Secure email required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Institutional Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="name@firm.com" {...field} className="bg-background/50 border-white/10 rounded-sm focus:ring-primary/50 font-sans" />
              </FormControl>
              <FormMessage className="font-mono text-[10px]" />
            </FormItem>} />
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Direct Line <span className="opacity-50">(Optional)</span></FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (000) 000-0000" {...field} className="bg-background/50 border-white/10 rounded-sm focus:ring-primary/50 font-sans" />
              </FormControl>
              <FormMessage className="font-mono text-[10px]" />
            </FormItem>} />

        <FormField control={control} name="reason" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Entity Type</FormLabel>
              <FormControl>
                <div className="relative">
                  <select className="flex h-10 w-full items-center justify-between rounded-sm border border-white/10 bg-background/50 px-3 py-2 text-sm shadow-xs ring-offset-background placeholder:text-muted-foreground focus:outline-hidden focus:ring-1 focus:ring-primary/50 disabled:cursor-not-allowed disabled:opacity-50 appearance-none font-sans" {...field} value={field.value || ""}>
                    <option value="" disabled>Select entity</option>
                    {["Institutional", "Family Office", "Venture Fund", "Sovereign Wealth", "Other"].map((opt, i) => <option key={i} value={opt} data-index={i} className="bg-background">{opt}</option>)}
                  </select>
                  <div className="absolute right-3 top-3 pointer-events-none opacity-50">
                    <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              </FormControl>
              <FormMessage className="font-mono text-[10px]" />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Interest specification required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Investment Interest</FormLabel>
            <FormControl>
              <Textarea placeholder="Specify infrastructure segments or capital allocation goals..." className="min-h-[120px] bg-background/50 border-white/10 rounded-sm focus:ring-primary/50 font-sans resize-none" {...field} />
            </FormControl>
            <FormMessage className="font-mono text-[10px]" />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-sm text-[10px] font-mono text-destructive flex items-center gap-2 uppercase tracking-wider">
          <span className="font-bold">Error:</span> {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-xs font-mono uppercase tracking-[0.2em] bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_color-mix(in_srgb,var(--color-primary),transparent_70%)] transition-all duration-300 rounded-sm">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            PROCESSING TRANSMISSION...
          </> : <>
            Authorize Submission
            <Send className="ml-2 size-3" />
          </>}
      </Button>
    </Form>;
}